import React, { useState } from 'react';
import { Heart, Clock, DoubleCheck, ChatLines } from 'iconoir-react';
import { useNavigate } from 'react-router-dom';
import { Button, Toaster } from '../common';
import { useMyInterests, useExpressInterest } from '../../hooks/useDiscover';
import { startDirectMessage } from '../../services/chatServices';
import { routes } from '../../lib/routes';
import toast from 'react-hot-toast';

/**
 * Jun-16 (a user V1 test, D6): "I also want to see when someone connects with
 * me." Surfaces received interests (people who expressed interest in the
 * current user) with a one-tap Connect back.
 *
 * PRIVACY: Discover is anonymous-until-mutual ("names are revealed only when
 * both sides reciprocate"). So a still-PENDING received interest must NOT reveal
 * the sender's identity — we show that *someone* is interested, plus their
 * optional message, and a Connect button. Connecting back reciprocates, creates
 * the mutual match, and only THEN are names revealed (in Matches). The sender's
 * id is used solely to fire the reciprocal interest mutation; it is never shown.
 *
 * Renders nothing when there are no pending received interests, so it never
 * clutters the page.
 */
const getTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const diffDays = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  const weeks = Math.floor(diffDays / 7);
  if (diffDays < 30) return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
  const months = Math.floor(diffDays / 30);
  return `${months} month${months === 1 ? '' : 's'} ago`;
};

const ReceivedInterests: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading } = useMyInterests({ type: 'received', page: 1, limit: 10 });
  const { mutate: expressInterest, isPending } = useExpressInterest();
  const [messagingId, setMessagingId] = useState<string | null>(null);

  const interests = ((data?.result?.interests as any[]) || []).filter(
    i => i?.status === 'pending',
  );

  if (isLoading || interests.length === 0) return null;

  const handleConnect = (fromUserId: string) => {
    if (!fromUserId) return;
    expressInterest(
      { to_user_id: fromUserId },
      {
        onSuccess: (response: any) => {
          const message = response?.result?.match_created
            ? "You're connected! See who in your Matches."
            : 'Interest sent back.';
          toast.custom(t => <Toaster message={message} type="success" id={t.id} />);
        },
        onError: (error: any) => {
          toast.custom(t => (
            <Toaster
              message={error?.message || 'Failed to connect'}
              type="error"
              id={t.id}
            />
          ));
        },
      },
    );
  };

  // R2-4b (a user round 2): inbound requests from people who are already MATCHES
  // are not anonymous (their names show in Matches), so reveal the name and open
  // the chat directly instead of only "connect back".
  const handleMessage = async (userId: string, matchId: string) => {
    if (!userId || !matchId) return;
    setMessagingId(userId);
    try {
      const conversationId = await startDirectMessage(userId, matchId);
      if (conversationId) {
        navigate(`${routes.chat}?channel=${conversationId}`);
      } else {
        toast.custom(t => <Toaster message="Could not open conversation" type="error" id={t.id} />);
      }
    } catch (e: any) {
      toast.custom(t => (
        <Toaster message={e?.message || 'Could not open conversation'} type="error" id={t.id} />
      ));
    } finally {
      setMessagingId(null);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-purple-25 p-4 mb-6">
      <h3 className="font-semibold text-heading-500 mb-1 flex items-center gap-2">
        <Heart className="w-5 h-5 text-primary-500" /> Interested in you
        <span className="text-xs font-medium bg-primary-50 text-primary-600 px-2 py-0.5 rounded-full">
          {interests.length}
        </span>
      </h3>
      <p className="text-sm text-body-400 mb-3">
        People who want to connect with you. Your matches show who they are, message them directly; for others, connect back to reveal them and start a conversation.
      </p>
      <div className="space-y-2">
        {interests.map(i => {
          const fromId = i.from_user?.id;
          // R2-4b: only people who are already MATCHES get their name + a direct
          // "Message" action revealed (match identities are not anonymous);
          // pure anonymous-discover interests keep the anonymous treatment.
          const matchName =
            i.is_match && i.from_user
              ? `${i.from_user.first_name || ''} ${i.from_user.last_name || ''}`.trim()
              : '';
          const isMatch = !!matchName && !!i.match_id;
          return (
            <div
              key={i.id}
              className="flex items-center justify-between gap-3 border border-gray-100 rounded-lg p-3"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-heading-600 truncate">
                  {isMatch ? `${matchName} wants to connect` : 'A member is interested in connecting'}
                </p>
                {isMatch && (
                  <p className="text-[11px] font-medium text-primary-500 mt-0.5">
                    One of your matches
                  </p>
                )}
                {i.message && (
                  <p className="text-xs text-body-400 truncate">"{i.message}"</p>
                )}
                {i.created_at && (
                  <p className="text-[11px] text-body-300 flex items-center gap-1 mt-0.5">
                    <Clock className="w-3 h-3" /> {getTimeAgo(i.created_at)}
                  </p>
                )}
              </div>
              {isMatch ? (
                <Button
                  size="sm"
                  className="flex-shrink-0"
                  isLoading={messagingId === fromId}
                  onClick={() => handleMessage(fromId, i.match_id)}
                >
                  <ChatLines className="w-4 h-4" /> Message
                </Button>
              ) : (
                <Button
                  size="sm"
                  className="flex-shrink-0"
                  isLoading={isPending}
                  onClick={() => handleConnect(fromId)}
                >
                  <DoubleCheck className="w-4 h-4" /> Connect
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ReceivedInterests;
